// Copyright (c) 2026 IOTA Stiftung
// SPDX-License-Identifier: Apache-2.0

import { useMemo } from 'react';
import { formatDate, TruncatedBalance } from '@iota/core';
import { formatAmount, formatBalance, CoinFormat, IOTA_DECIMALS } from '@iota/iota-sdk/utils';
import { LabelTextSize } from '@iota/apps-ui-kit';
import { GraphTooltipContent } from '../GraphTooltipContent';
import { StatisticsPanel } from '../StatisticsPanel';
import {
    useEpochMetricsHistory,
    sampleEpochs,
    nanosToIota,
    EPOCH_METRICS_SAMPLE_INTERVAL,
    type CompletedEpochMetrics,
} from '~/hooks';

function TooltipContent({ data }: { data: CompletedEpochMetrics }): JSX.Element {
    const date = formatDate(new Date(Number(data.epochStartTimestamp)), ['day', 'month']);
    const { storageCharge, storageRebate } = data.endOfEpochInfo;
    const net = BigInt(storageCharge) - BigInt(storageRebate);
    return (
        <GraphTooltipContent
            overline={`${date}, Epoch ${data.epoch}`}
            title={`${formatBalance(storageCharge, IOTA_DECIMALS, CoinFormat.Full)} IOTA charged`}
            subtitle={`Rebate ${formatBalance(storageRebate, IOTA_DECIMALS, CoinFormat.Full)} · Net ${formatBalance(net, IOTA_DECIMALS, CoinFormat.Full, true)} IOTA`}
        />
    );
}

export function StorageHistoryChart(): JSX.Element {
    const { data: completedEpochs, isPending } = useEpochMetricsHistory();

    const { chartData, totalCharge, totalRebate } = useMemo(() => {
        const epochs = completedEpochs ?? [];
        return {
            chartData: sampleEpochs(epochs, EPOCH_METRICS_SAMPLE_INTERVAL),
            totalCharge: epochs.reduce(
                (sum, epoch) => sum + BigInt(epoch.endOfEpochInfo.storageCharge),
                0n,
            ),
            totalRebate: epochs.reduce(
                (sum, epoch) => sum + BigInt(epoch.endOfEpochInfo.storageRebate),
                0n,
            ),
        };
    }, [completedEpochs]);

    return (
        <StatisticsPanel
            title="Storage Charge vs Rebate"
            data={chartData}
            isPending={isPending}
            stats={[
                {
                    size: LabelTextSize.Large,
                    label: 'Total Charge',
                    text: (
                        <TruncatedBalance
                            balance={totalCharge}
                            decimals={IOTA_DECIMALS}
                            symbol="IOTA"
                        />
                    ),
                    supportingLabel: 'IOTA',
                    fiatAmount: totalCharge,
                },
                {
                    size: LabelTextSize.Large,
                    label: 'Total Rebate',
                    text: (
                        <TruncatedBalance
                            balance={totalRebate}
                            decimals={IOTA_DECIMALS}
                            symbol="IOTA"
                        />
                    ),
                    supportingLabel: 'IOTA',
                    fiatAmount: totalRebate,
                },
                {
                    size: LabelTextSize.Large,
                    label: 'Net',
                    text: (
                        <TruncatedBalance
                            balance={totalCharge - totalRebate}
                            decimals={IOTA_DECIMALS}
                            symbol="IOTA"
                            showSign
                        />
                    ),
                    supportingLabel: 'IOTA',
                    fiatAmount: totalCharge - totalRebate,
                },
            ]}
            getX={({ epoch }) => Number(epoch)}
            getY={({ endOfEpochInfo }) => nanosToIota(endOfEpochInfo.storageCharge)}
            formatY={formatAmount}
            tooltipContent={TooltipContent}
        />
    );
}
